import { BrowserSupport } from '@tableau/browser-support';
import * as _ from 'underscore';

import { ListResult } from '../fetchers/ListResult';
import { IColumnWidths } from '../models/ColumnWidths';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { ILayout } from '../models/ILayout';
import { ISliceable } from '../models/ISliceable';
import { ITemplate } from '../models/ITemplate';
import { ListLayout } from '../models/ListLayout';
import { IScrollControls } from './ScrollCtrl';

export interface IDataGridBodyScope extends ng.IScope {
  // Bound to attributes
  cells: ITemplate[];
  columnWidths: IColumnWidths;
  contentWidthsChanged: (locals: { contentWidths: number[], containerWidth: number}) => any;
  rowHeight?: number;
  isRowSelected: (locals: { row: any }) => boolean;
  rowClicked: (locals: { row: any, colIndex?: number }) => any;
  rowHovered: (locals: { row: any, colIndex?: number }) => any;
  rows: ISliceable<any>;
  currentRowIndex: number;
  currentRowObject: any;
  sliceChanged: (locals: { newSlice: ListResult<any> }) => any;
  alwaysHaveCurrentRow: boolean;
  scrollControls: IScrollControls;
  scrollLeftChanged: (locals: { scrollLeft: number }) => any;
  emptyTemplate: string;
  emptyTemplateModel: IEmptyTemplateModel;
  keepColumnWidths: boolean;
  rowsClickable: boolean;

  // Added in controller
  empty: boolean;
  layout: ILayout;
  handleSliceChanged: (newSlice: ListResult<any>) => any;
  colIndex(getChildFn: () => JQuery): number;
  rowIndex(getChildFn: () => JQuery): number;
  setHoverRow(rowElement: () => JQuery, locals: { row: any, colIndex?: number }): void;
  setHoverRowIndex(rowIndex: number, locals: { row: any, colIndex?: number }): void;

  setActiveRow(getRowFn: () => JQuery): void;
  setActiveRowIfHoverRow(getRowFn: () => JQuery): void;
  clearActiveRow(): void;

  isCurrentRowIndex: (rowIndex: number) => boolean;
  getElementsOfSelectedRows: () => JQuery;
}

export class DataGridBodyCtrl {

  public static $inject = ['$scope', '$element', '$timeout'];

  private $scope: IDataGridBodyScope;
  private $element: JQuery;
  private $timeout: ng.ITimeoutService;
  private hoverRow: JQuery;
  private activeRow: JQuery;
  private slice: ListResult<any>;
  $onInit: () => void;

  constructor($scope: IDataGridBodyScope, $element: JQuery,
              $timeout: ng.ITimeoutService) {
    this.$onInit = () => {
      this.$scope = $scope;
      this.$element = $element;
      this.$timeout = $timeout;

      $scope.layout = new ListLayout($scope.rowHeight);

      if (!$scope.keepColumnWidths) {
        this.$element.css('visibility', 'hidden');
      }

      $scope.handleSliceChanged = (newSlice: ListResult<any>) => {
        const prevSlice = this.slice;
        const hadItems = prevSlice && prevSlice.items && prevSlice.items.length > 0;

        this.slice = newSlice;
        const haveItems = newSlice && newSlice.items && newSlice.items.length > 0;
        $scope.empty = !haveItems;
        $scope.emptyTemplateModel = $scope.emptyTemplateModel || {};

        this.ensureCurrentRowIndexInRange();

        if (!$scope.keepColumnWidths) {
          if (!hadItems) {
            if (haveItems) {
              // We are going from no items to some items, so reset the actual widths.
              // Otherwise, the default widths will stick, but we want to resize based on content.
              if ($scope.columnWidths && $scope.columnWidths.resetActual) {
                this.$element.css('visibility', 'hidden');
                this.$scope.columnWidths.resetActual();
              }
              // Once the new content has loaded, measure the new widths.
              this.$timeout(() => {
                const contentWidths = _.map(this.$element.find('.tb-data-grid-body-column'), (column: Element) => {
                  const rect = column.getBoundingClientRect();
                  return Math.ceil(rect.right - rect.left); // to correct for non-integer widths
                });
                this.$scope.contentWidthsChanged({ contentWidths: contentWidths, containerWidth: this.getGridWidth() });
                this.$element.css('visibility', 'visible');
              });
            } else {
              if (!prevSlice) {
                // We got a slice with no items on first load.
                // We need to size the columns, but we have no content to base the sizes on,
                // so pick some reasonable defaults.
                const containerWidth = this.getGridWidth();
                let remainingSpace = containerWidth;
                const contentWidths: number[] = [];
                for (let i = 0, l = $scope.cells.length; i < l; i++) {
                  const cell = $scope.cells[i];
                  // If the column has a default width specified, prefer that.
                  let contentWidth = parseInt(cell.attrs['defaultWidth'], 10);
                  if (!(contentWidth >= 0)) {
                    // If it doesn't, give it a proportional amount of the remaining space.
                    contentWidth = remainingSpace / (l - i);
                  }
                  // Take the minimum width of the column into account.
                  if ($scope.columnWidths && $scope.columnWidths.getMin) {
                    contentWidth = Math.max(contentWidth, $scope.columnWidths.getMin(i));
                  }
                  contentWidths.push(contentWidth);
                  remainingSpace -= contentWidth;
                }
                this.$scope.contentWidthsChanged({ contentWidths: contentWidths, containerWidth: containerWidth });
              }
              this.$element.css('visibility', 'visible');
            }
          }
        }

        if (this.$scope.sliceChanged) {
          this.$scope.sliceChanged({ newSlice: newSlice });
        }

        this.updateCurrentRowObject();
      };

      $scope.colIndex = (getChildFn: () => JQuery) => {
        return parseInt(getChildFn().attr('data-col-index'), 10);
      };
      $scope.rowIndex = (getChildFn: () => JQuery) => {
        return parseInt(getChildFn().attr('data-row-index'), 10);
      };

      $scope.setHoverRow = (getRowElement: () => JQuery, locals: { row: any, colIndex?: number }) => {
        if (this.hoverRow) {
          this.hoverRow.removeClass('tb-data-grid-hover-row');
        }
        this.hoverRow = getRowElement ? getRowElement() : null;
        if (this.hoverRow && !BrowserSupport.IsTouch()) {
          this.hoverRow.addClass('tb-data-grid-hover-row');
        }
        $scope.rowHovered(locals);
      };

      $scope.isCurrentRowIndex = (rowIndex: number) => {
        return $scope.currentRowIndex === this.slice.index + rowIndex;
      };

      $scope.setActiveRow = (getRowFn: () => JQuery) => {

        this.clearActiveRow();
        const row = getRowFn();
        if (row && $scope.rowsClickable) {
          const rowIndex = $scope.rowIndex(getRowFn);

          // Set the "current" row to the active row
          $scope.currentRowIndex = this.slice.index + rowIndex;

          // Update class on active background row
          this.activeRow = this.getBackgroundRow(rowIndex).addClass('tb-data-grid-active-row');
        }
      };

      $scope.setActiveRowIfHoverRow = (getRowFn: () => JQuery) => {
        if (this.hoverRow) {
          $scope.setActiveRow(getRowFn);
        }
      };

      $scope.clearActiveRow = () => this.clearActiveRow();

      $scope.setHoverRowIndex = (rowIndex: number, locals: { row: any, colIndex?: number }) => {
        $scope.setHoverRow(() => this.getBackgroundRow(rowIndex), locals);
      };

      $scope.getElementsOfSelectedRows = () => {
        const selectedIndices: number[] = [];
        _.each(this.slice.items, (row, index) => {
          if (this.$scope.isRowSelected({ row: row })) {
            selectedIndices.push(index);
          }
        });
        return this.getBackgroundRows(selectedIndices);
      };

      $scope.$watch('currentRowIndex', () => {

        this.ensureCurrentRowIndexInRange();

        if ($scope.currentRowIndex >= 0 && $scope.scrollControls) {
          $scope.scrollControls.scrollIntoView($scope.currentRowIndex);
        }

        this.updateCurrentRowObject();
      });
    };
  }

  private clearActiveRow() {
    if (this.activeRow) {
      this.activeRow.removeClass('tb-data-grid-active-row');
      this.activeRow = null;
    }
  }

  private ensureCurrentRowIndexInRange() {
    let idx = this.$scope.currentRowIndex;

    if (this.slice && _.isFinite(idx)) {
      idx = Math.min(Math.max(-1, idx), this.slice.total - 1);
      if (this.$scope.alwaysHaveCurrentRow) {
        idx = Math.max(0, idx);
      }
    } else {
      idx = -1;
    }
    this.$scope.currentRowIndex = idx;
  }

  /**
   * Get width of the usable grid area (scrollbar not included)
   */
  private getGridWidth(): number {
    // An optional left margin can be added to the body columns, so we have to take that into account.
    // See tb-data-grid-margin-left.
    const backgroundWidth = this.$element.find('.tb-data-grid-body-background').width();
    const marginWidth = parseInt(this.$element.find('.tb-data-grid-body-columns').css('margin-left'), 10) || 0;
    return backgroundWidth - marginWidth;
  }

  private getBackgroundRow(rowIndex: number): JQuery {
    return this.$element.find('.tb-data-grid-row-background').eq(rowIndex);
  }

  private getBackgroundRows(rowIndices: number[]): JQuery {
    return this.$element.find('.tb-data-grid-row-background').filter((index: number) => {
      // Note: not using _.contains because it performs a linear search.
      return _.indexOf(rowIndices, index, true) >= 0;
    });
  }

  private updateCurrentRowObject() {
    if (!this.slice || this.$scope.currentRowIndex < 0) {
      this.$scope.currentRowObject = null;
    } else {
      this.$scope.currentRowObject = this.slice.items[this.$scope.currentRowIndex - this.slice.index];
    }
  }

}
